import { createFileRoute, redirect, useRouter, useSearch } from '@tanstack/react-router';
import { APIError, useAuth } from '@vizit/auth';
import { AuthFooter, ResetPasswordError, ResetPasswordForm, type ResetPasswordFormSchema } from '@vizit/organisms';
import * as React from 'react';

type ResetPasswordSearch = {
  'user-id': string;
  token: string;
};

export const Route = createFileRoute('/_auth/reset-password')({
  staticData: {
    pageName: 'Reset Password',
  },
  component: RouteComponent,
  beforeLoad: async ({ search, context }) => {
    // Only allow access to this route if a user_id is provided.
    if ((!search['user-id'] || !search.token) && !context?.auth?.isAuthenticated) {
      throw redirect({
        to: '/login',
      });
    }
  },
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => {
    // Validate and parse the search params into a typed state
    return {
      'user-id': (search['user-id'] as string) || '',
      token: (search.token as string) || '',
    };
  },
});

function RouteComponent() {
  const router = useRouter();
  const { 'user-id': userId, token } = useSearch({ from: Route.id });

  const { onSetPassword } = useAuth();

  const [hasSubmissionError, setHasSubmissionError] = React.useState<null | ResetPasswordError>(null);

  const onSubmit = async (data: ResetPasswordFormSchema) => {
    const { password: new_password } = data;

    const onSuccess = () => {
      router.navigate({
        to: '/',
      });
    };

    const onError = (error: unknown | APIError) => {
      if (error instanceof APIError && JSON.parse(error.message).detail === 'Reset token is expired') {
        setHasSubmissionError(ResetPasswordError.InvalidToken);
      } else {
        setHasSubmissionError(ResetPasswordError.Default);
      }
    };

    await onSetPassword(userId, token, new_password, onSuccess, onError);
  };

  return (
    <div className="h-screen w-screen flex flex-col justify-center items-center">
      <ResetPasswordForm onSubmit={onSubmit} hasSubmissionError={hasSubmissionError} />
      <AuthFooter />
    </div>
  );
}
